import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Center,
  Flex,
  Image,
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import dominus_logo_transparent_white from '../assets/dominus_logo_transparent_white.png';
import UsernamePasswordSignIn from '../components/UsernamePasswordSignIn';
import { getAuthParams } from '../constants';
import VisitorFooter from '../components/VisitorFooter';
import GlassCard from '../components/GlassCard';
import apiClient from '../lib/api/apiClient';

const GoogleLogo = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g clipPath="url(#clip0_17_40)">
      <path d="M23.766 12.276c0-.818-.074-1.604-.213-2.356H12.24v4.451h6.484a5.54 5.54 0 01-2.4 3.632v3.017h3.877c2.27-2.09 3.565-5.17 3.565-8.744z" fill="#4285F4" />
      <path d="M12.24 24c3.24 0 5.963-1.07 7.95-2.91l-3.877-3.017c-1.08.726-2.46 1.16-4.073 1.16-3.13 0-5.78-2.11-6.73-4.946H1.53v3.09A11.997 11.997 0 0012.24 24z" fill="#34A853" />
      <path d="M5.51 14.287a7.19 7.19 0 010-4.574V6.623H1.53a12.002 12.002 0 000 10.754l3.98-3.09z" fill="#FBBC05" />
      <path d="M12.24 4.77c1.76 0 3.34.605 4.59 1.793l3.43-3.43C18.2 1.07 15.48 0 12.24 0A11.997 11.997 0 001.53 6.623l3.98 3.09c.95-2.836 3.6-4.946 6.73-4.946z" fill="#EA4335" />
    </g>
    <defs>
      <clipPath id="clip0_17_40">
        <rect width="24" height="24" fill="white" />
      </clipPath>
    </defs>
  </svg>
);

const SignInPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/dashboard';
  const [isLoading, setIsLoading] = useState(false);
  const { isOpen: isForgotOpen, onOpen: onForgotOpen, onClose: onForgotClose } = useDisclosure();
  const [forgotEmail, setForgotEmail] = useState('');
  const [isSendingReset, setIsSendingReset] = useState(false);
  const toast = useToast();

  const handleForgotPassword = async () => {
    if (!forgotEmail.trim() || !/.+@.+\..+/.test(forgotEmail)) {
      toast({
        title: 'Invalid Email',
        description: 'Please enter a valid email address.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
      return;
    }
    try {
      setIsSendingReset(true);
      await apiClient.post('/email/forgot-password', { email: forgotEmail });
      toast({
        title: 'Reset Email Sent',
        description: 'If an account exists with that email, you will receive a password reset link.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      setForgotEmail('');
      onForgotClose();
    } catch (error) {
      toast({
        title: 'Request Failed',
        description: 'Unable to send reset email. Please try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSendingReset(false);
    }
  };

  const handleForgotClose = () => {
    if (!isSendingReset) {
      setForgotEmail('');
      onForgotClose();
    }
  };

  const handleGoogleSignIn = () => {
    setIsLoading(true);
    let authUrl = getAuthParams()
    const pendingInvite = localStorage.getItem('pendingInvite');
    if (pendingInvite) {
      authUrl += `&pendingInvite=${pendingInvite}`;
    }
    window.location.href = authUrl;
  };

  return (
    <Box
      minH="100vh"
      w="100%"
      display="flex"
      flexDirection="column"
      position="absolute"
      top="0"
      left="0"
      bgGradient="linear(to-b, black, #002e0b)"
      color="white"
      fontFamily="'Exo 2', sans-serif"
    >
      {/* Navigation Bar */}
      <Box
        as="nav"
        position="fixed"
        top={0}
        w="100%"
        bg="transparent"
        backdropFilter="blur(8px)"
        zIndex={50}
      >
        <Flex justifyContent="space-between" maxW="8xl" mx="auto" px={4} py={4}>
          <Box flexShrink={0}>
            <Link to="/">
              <Image src={dominus_logo_transparent_white} alt="Dominus" h={12} />
            </Link>
          </Box>
          <Box>
            <Button
              onClick={() => navigate('/signup')}
              variant="dominus"
            >
              Register
            </Button>
          </Box>
        </Flex>
      </Box>

      {/* Sign In Content */}
      <Container maxW="600px" pt={32} flex="1 0 auto">
        <Center minH="60vh">
          <VStack gap={8} width="full">
            <VStack gap={3} textAlign="center" w="full">
              <Box w="full" maxW="600px" mx="auto">
                <Heading
                  as="h1"
                  size="xl"
                  fontFamily="Audiowide"
                  fontWeight="bold"
                  color="white"
                >
                  Sign In To<br />
                  <span style={{ whiteSpace: 'nowrap' }}>Dominus Baseball</span>
                </Heading>
              </Box>
            </VStack>

            <GlassCard p={8} w="full" textAlign="center" baseColor="#002e0b">
              <VStack gap={6}>
                <Button
                  onClick={handleGoogleSignIn}
                  w="full"
                  variant="dominus"
                  py={4}
                  fontSize="xl"
                  isLoading={isLoading}
                  disabled={isLoading}
                  loadingText="Redirecting..."
                >
                  {!isLoading && (
                    <Flex align="center" justify="center" gap={3}>
                      <GoogleLogo />
                      Sign In With Google
                    </Flex>
                  )}
                </Button>
                <Text
                  color="white"
                  fontSize="2xl"
                  fontWeight="bold"
                  opacity={0.85}
                  my={2}
                >
                  or
                </Text>
                <UsernamePasswordSignIn onSuccess={() => navigate(from, { replace: true })} />
                <Text
                  as="button"
                  type="button"
                  color="whiteAlpha.700"
                  fontSize="sm"
                  cursor="pointer"
                  _hover={{ color: 'white', textDecoration: 'underline' }}
                  onClick={onForgotOpen}
                  mt={-2}
                >
                  Forgot Password?
                </Text>
              </VStack>
            </GlassCard>
          </VStack>
        </Center>
      </Container>
      {/* Forgot Password Modal */}
      <Modal isOpen={isForgotOpen} onClose={handleForgotClose} isCentered>
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader>Reset Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4} fontSize="sm" color="whiteAlpha.800">
              Enter your email address and we'll send you a link to reset your password.
            </Text>
            <FormControl>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                placeholder="you@example.com"
                value={forgotEmail}
                onChange={(e) => setForgotEmail(e.target.value.toLowerCase())}
                bg="transparent"
                borderColor="whiteAlpha.400"
                borderWidth="1.5px"
                _hover={{ borderColor: 'whiteAlpha.600' }}
                _focus={{ borderColor: 'white' }}
                onKeyDown={(e) => { if (e.key === 'Enter') handleForgotPassword(); }}
                disabled={isSendingReset}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              mr={3}
              onClick={handleForgotClose}
              isDisabled={isSendingReset}
              color="whiteAlpha.700"
              _hover={{ color: 'white', bg: 'whiteAlpha.200' }}
            >
              Cancel
            </Button>
            <Button
              variant="dominus"
              onClick={handleForgotPassword}
              isLoading={isSendingReset}
              loadingText="Sending..."
            >
              Send Reset Link
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <VisitorFooter />
    </Box>
  );
};

export default SignInPage; 